import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  FileText,
  Download,
  Loader2,
  CheckCircle,
  AlertCircle,
  Users,
  Package,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { supabase } from '../../lib/supabase';
import {
  generateLivret,
  generateLivretClasse,
  checkCompleteness,
  clearTemplateCache,
  CompletenessReport,
  LivretGenerationError,
} from '../../lib/livretGenerator';
import { generateLivretPdf } from '../../lib/livretPdfGenerator';

type ClassInfo = {
  id: string;
  name: string;
  class_number: number;
  period: string;
};

type BatchStatus =
  | { kind: 'idle' }
  | { kind: 'running'; current: number; total: number; studentName: string }
  | { kind: 'done'; generated: number; errors: { student: string; error: string }[] }
  | { kind: 'error'; message: string };

const STATUS_LABELS: Record<CompletenessReport['status'], { label: string; color: string; icon: string }> = {
  complete: { label: 'Complet', color: 'bg-success-50 text-success-700 border-success-200', icon: '🟢' },
  partial: { label: 'Partiel', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: '🟡' },
  incomplete: { label: 'Incomplet', color: 'bg-error-50 text-error-700 border-error-200', icon: '🔴' },
};

const MISSING_LABELS: Record<string, string> = {
  numero_candidat: 'N° candidat',
  date_naissance: 'Date de naissance',
  lieu_naissance: 'Lieu de naissance',
  INE: 'INE',
  LV1: 'Langue vivante 1',
  session: 'Session',
  avis_conseil_classe: 'Avis conseil de classe',
};

const AdminLivretsBatch = () => {
  const { classId } = useParams<{ classId: string }>();
  const [classInfo, setClassInfo] = useState<ClassInfo | null>(null);
  const [sessionYear, setSessionYear] = useState<number>(new Date().getFullYear() + 1);
  const [reports, setReports] = useState<CompletenessReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [batchStatus, setBatchStatus] = useState<BatchStatus>({ kind: 'idle' });
  const [individualBusy, setIndividualBusy] = useState<string | null>(null);
  const [individualBusyKind, setIndividualBusyKind] = useState<'pdf' | 'xlsx' | null>(null);

  useEffect(() => {
    if (!classId) return;
    void init();
  }, [classId]);

  useEffect(() => {
    if (classId && sessionYear) {
      void reloadReports();
    }
  }, [sessionYear]);

  const init = async () => {
    try {
      setIsLoading(true);
      const { data: cls } = await supabase
        .from('classes')
        .select('id, name, class_number, period')
        .eq('id', classId)
        .maybeSingle();
      if (cls) setClassInfo(cls as ClassInfo);

      // Déduire la session la plus probable à partir des étudiants de la classe
      const { data: students } = await supabase
        .from('students')
        .select('session_examen')
        .eq('class_id', classId);
      const sessions = (students ?? [])
        .map((s: any) => s.session_examen)
        .filter((v: unknown) => typeof v === 'number');
      if (sessions.length > 0) {
        const counts = new Map<number, number>();
        for (const y of sessions) counts.set(y, (counts.get(y) ?? 0) + 1);
        const [topSession] = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0];
        setSessionYear(topSession);
      }

      await reloadReports();
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const reloadReports = async () => {
    if (!classId) return;
    const data = await checkCompleteness(classId, sessionYear);
    setReports(data);
  };

  // Génération d'un livret PDF individuel (action principale côté étudiant)
  const downloadOnePdf = async (studentId: string, displayName: string) => {
    setIndividualBusy(studentId);
    setIndividualBusyKind('pdf');
    try {
      const { blob, filename } = await generateLivretPdf(studentId);
      saveAs(blob, filename);
    } catch (e) {
      const msg =
        e instanceof LivretGenerationError ? JSON.stringify(e.detail) : (e as Error).message;
      alert(`Échec PDF pour ${displayName} : ${msg}`);
    } finally {
      setIndividualBusy(null);
      setIndividualBusyKind(null);
    }
  };

  // Génération du livret Excel individuel (admin seulement, pour le cas où il a besoin
  // du fichier officiel xlsx complet pour un candidat spécifique)
  const downloadOneXlsx = async (studentId: string, displayName: string) => {
    setIndividualBusy(studentId);
    setIndividualBusyKind('xlsx');
    try {
      const { blob, filename } = await generateLivret(studentId);
      saveAs(blob, filename);
    } catch (e) {
      const msg =
        e instanceof LivretGenerationError ? JSON.stringify(e.detail) : (e as Error).message;
      alert(`Échec Excel pour ${displayName} : ${msg}`);
    } finally {
      setIndividualBusy(null);
      setIndividualBusyKind(null);
    }
  };

  // Génération batch = un fichier ZIP contenant un PDF par étudiant (format destiné aux étudiants)
  const downloadBatchZipPdf = async () => {
    if (!classId) return;
    const eligible = reports.filter((r) => r.numero_candidat && r.status !== 'incomplete');
    if (eligible.length === 0) {
      setBatchStatus({
        kind: 'error',
        message:
          "Aucun étudiant éligible (tous sont 'incomplet' ou sans numéro de candidat).",
      });
      return;
    }
    setBatchStatus({
      kind: 'running',
      current: 0,
      total: eligible.length,
      studentName: eligible[0].first_name + ' ' + eligible[0].last_name,
    });
    const zip = new JSZip();
    const errors: { student: string; error: string }[] = [];
    let generated = 0;

    for (let i = 0; i < eligible.length; i++) {
      const r = eligible[i];
      const displayName = `${r.last_name} ${r.first_name}`;
      setBatchStatus({
        kind: 'running',
        current: i + 1,
        total: eligible.length,
        studentName: displayName,
      });
      try {
        const { blob, filename } = await generateLivretPdf(r.student_id);
        const ab = await blob.arrayBuffer();
        zip.file(filename, ab);
        generated += 1;
      } catch (e) {
        const msg =
          e instanceof LivretGenerationError ? JSON.stringify(e.detail) : (e as Error).message;
        errors.push({ student: displayName, error: msg });
      }
    }

    if (generated === 0) {
      setBatchStatus({ kind: 'done', generated: 0, errors });
      return;
    }

    try {
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const classLabel = classInfo
        ? `${classInfo.name}_${classInfo.class_number}`.replace(/[^A-Za-z0-9_-]/g, '_')
        : 'classe';
      saveAs(zipBlob, `livrets_pdf_${classLabel}_${sessionYear}.zip`);
    } catch (e) {
      errors.push({ student: 'ZIP', error: (e as Error).message });
    }

    setBatchStatus({ kind: 'done', generated, errors });
  };

  // Génération batch ZIP Excel — admin seulement, fichier xlsx par étudiant
  const downloadBatchZipXlsx = async () => {
    if (!classId) return;
    const eligible = reports.filter((r) => r.numero_candidat && r.status !== 'incomplete');
    if (eligible.length === 0) {
      setBatchStatus({
        kind: 'error',
        message:
          "Aucun étudiant éligible (tous sont 'incomplet' ou sans numéro de candidat).",
      });
      return;
    }
    clearTemplateCache();
    setBatchStatus({
      kind: 'running',
      current: 0,
      total: eligible.length,
      studentName: eligible[0].first_name + ' ' + eligible[0].last_name,
    });
    const zip = new JSZip();
    const errors: { student: string; error: string }[] = [];
    let generated = 0;

    for (let i = 0; i < eligible.length; i++) {
      const r = eligible[i];
      const displayName = `${r.last_name} ${r.first_name}`;
      setBatchStatus({
        kind: 'running',
        current: i + 1,
        total: eligible.length,
        studentName: displayName,
      });
      try {
        const { blob, filename } = await generateLivret(r.student_id);
        const ab = await blob.arrayBuffer();
        zip.file(filename, ab);
        generated += 1;
      } catch (e) {
        const msg =
          e instanceof LivretGenerationError ? JSON.stringify(e.detail) : (e as Error).message;
        errors.push({ student: displayName, error: msg });
      }
    }

    if (generated === 0) {
      setBatchStatus({ kind: 'done', generated: 0, errors });
      return;
    }

    try {
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const classLabel = classInfo
        ? `${classInfo.name}_${classInfo.class_number}`.replace(/[^A-Za-z0-9_-]/g, '_')
        : 'classe';
      saveAs(zipBlob, `livrets_xlsx_${classLabel}_${sessionYear}.zip`);
    } catch (e) {
      errors.push({ student: 'ZIP', error: (e as Error).message });
    }

    setBatchStatus({ kind: 'done', generated, errors });
  };

  // Livret de classe unique (un seul fichier xlsx avec tous les candidats)
  const downloadConsolidated = async () => {
    if (!classId) return;
    setBatchStatus({
      kind: 'running',
      current: 0,
      total: 1,
      studentName: 'Fichier unique classe',
    });
    try {
      const { blob, filename } = await generateLivretClasse(classId);
      saveAs(blob, filename);
      setBatchStatus({ kind: 'done', generated: 1, errors: [] });
    } catch (e) {
      const msg =
        e instanceof LivretGenerationError ? JSON.stringify(e.detail) : (e as Error).message;
      setBatchStatus({ kind: 'error', message: msg });
    }
  };

  const completeCount = reports.filter((r) => r.status === 'complete').length;
  const partialCount = reports.filter((r) => r.status === 'partial').length;
  const incompleteCount = reports.filter((r) => r.status === 'incomplete').length;
  const withCandidat = reports.filter((r) => r.numero_candidat).length;

  return (
    <div className="animate-fade-in">
      <header className="mb-8">
        <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-2">
          <ChevronLeft className="h-4 w-4 mr-1" />
          Retour au tableau de bord
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <FileText className="h-7 w-7 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-900">Livrets scolaires — Génération</h1>
        </div>
        <p className="text-gray-600">
          {classInfo && (
            <span className="font-medium">
              {classInfo.name} {classInfo.class_number} ({classInfo.period}) —{' '}
            </span>
          )}
          Aperçu du statut de complétude et génération en lot.
        </p>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Total</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{reports.length}</p>
          <p className="text-xs text-gray-500 mt-1">
            <Users className="inline h-3 w-3 mr-0.5" />
            {withCandidat} avec N° candidat
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-success-600 uppercase tracking-wide">🟢 Complet</p>
          <p className="text-2xl font-bold text-success-600 mt-1">{completeCount}</p>
          <p className="text-xs text-gray-500 mt-1">Génération OK</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-amber-600 uppercase tracking-wide">🟡 Partiel</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">{partialCount}</p>
          <p className="text-xs text-gray-500 mt-1">≤ 2 champs manquants</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-error-600 uppercase tracking-wide">🔴 Incomplet</p>
          <p className="text-2xl font-bold text-error-600 mt-1">{incompleteCount}</p>
          <p className="text-xs text-gray-500 mt-1">Exclus du batch</p>
        </div>
      </div>

      {/* Actions batch */}
      <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-lg p-6 text-white shadow-lg mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <Package className="h-8 w-8 flex-shrink-0 mt-1" />
            <div className="min-w-0">
              <h2 className="text-xl font-bold">Génération en lot</h2>
              <p className="opacity-90 mt-1 text-sm">
                <strong>PDF</strong> = récap individuel pour l'étudiant. <strong>Excel</strong> =
                fichier officiel (format Éducation Nationale) pour archivage / académie.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div>
              <label className="block text-xs opacity-80 mb-0.5">Session</label>
              <select
                value={sessionYear}
                onChange={(e) => setSessionYear(Number(e.target.value))}
                className="text-gray-900 rounded px-2 py-1 text-sm"
              >
                {[sessionYear - 1, sessionYear, sessionYear + 1, sessionYear + 2].map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={reloadReports}
              className="bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded text-sm inline-flex items-center gap-1"
              title="Rafraîchir"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            onClick={downloadBatchZipPdf}
            disabled={batchStatus.kind === 'running'}
            className="bg-white text-primary-700 font-semibold px-5 py-2.5 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            title="Un PDF par étudiant, à distribuer"
          >
            {batchStatus.kind === 'running' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            ZIP PDFs ({completeCount + partialCount} étudiants)
          </button>
          <button
            onClick={downloadBatchZipXlsx}
            disabled={batchStatus.kind === 'running'}
            className="bg-white/10 hover:bg-white/20 text-white font-semibold px-5 py-2.5 rounded-lg disabled:opacity-50 inline-flex items-center gap-2"
            title="Un fichier Excel officiel par étudiant"
          >
            <Package className="h-4 w-4" />
            ZIP Excels (admin)
          </button>
          <button
            onClick={downloadConsolidated}
            disabled={batchStatus.kind === 'running'}
            className="bg-white/10 hover:bg-white/20 text-white font-semibold px-5 py-2.5 rounded-lg disabled:opacity-50 inline-flex items-center gap-2"
            title="Fichier Excel officiel unique contenant tous les candidats (format Éducation Nationale)"
          >
            <FileText className="h-4 w-4" />
            Excel classe unique
          </button>
        </div>

        {batchStatus.kind === 'running' && (
          <div className="mt-4 bg-white/10 rounded p-3 text-sm">
            <div className="flex items-center justify-between mb-2">
              <span>
                Génération en cours : {batchStatus.current}/{batchStatus.total} —{' '}
                {batchStatus.studentName}
              </span>
              <span>{Math.round((batchStatus.current / batchStatus.total) * 100)}%</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-2">
              <div
                className="bg-white h-2 rounded-full transition-all"
                style={{
                  width: `${(batchStatus.current / batchStatus.total) * 100}%`,
                }}
              />
            </div>
          </div>
        )}

        {batchStatus.kind === 'done' && (
          <div className="mt-4 bg-white/10 rounded p-3 text-sm">
            <p className="inline-flex items-center">
              <CheckCircle className="h-4 w-4 mr-2" />
              {batchStatus.generated} livret{batchStatus.generated > 1 ? 's' : ''} généré
              {batchStatus.generated > 1 ? 's' : ''}.
            </p>
            {batchStatus.errors.length > 0 && (
              <div className="mt-2">
                <p className="font-medium">Erreurs :</p>
                <ul className="text-xs space-y-0.5 mt-1">
                  {batchStatus.errors.map((er, i) => (
                    <li key={i} className="opacity-90">
                      • <strong>{er.student}</strong> : {er.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {batchStatus.kind === 'error' && (
          <div className="mt-4 bg-white/10 rounded p-3 text-sm inline-flex items-start">
            <AlertCircle className="h-4 w-4 mr-2 mt-0.5" />
            {batchStatus.message}
          </div>
        )}
      </div>

      {/* Table des étudiants */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="font-semibold text-gray-900">Statut par étudiant</h2>
        </div>
        {isLoading ? (
          <div className="p-12 text-center text-gray-500">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary-600" />
          </div>
        ) : reports.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Aucun étudiant dans cette classe.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="px-4 py-2">N° Cand.</th>
                  <th className="px-4 py-2">Nom</th>
                  <th className="px-4 py-2">Statut</th>
                  <th className="px-4 py-2">Éléments manquants</th>
                  <th className="px-4 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reports.map((r) => {
                  const s = STATUS_LABELS[r.status];
                  const displayName = `${r.last_name.toUpperCase()} ${r.first_name}`;
                  const busy = individualBusy === r.student_id;
                  const busyPdf = busy && individualBusyKind === 'pdf';
                  const busyXlsx = busy && individualBusyKind === 'xlsx';
                  return (
                    <tr key={r.student_id}>
                      <td className="font-mono font-medium">{r.numero_candidat ?? '—'}</td>
                      <td>{displayName}</td>
                      <td>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${s.color}`}
                        >
                          <span>{s.icon}</span>
                          {s.label}
                        </span>
                      </td>
                      <td>
                        {r.missing.length === 0 ? (
                          <span className="text-xs text-gray-400">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {r.missing.map((m) => (
                              <span
                                key={m}
                                className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded"
                              >
                                {MISSING_LABELS[m] ?? m}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="text-right">
                        <div className="inline-flex gap-1.5 items-center justify-end">
                          {r.status === 'incomplete' && (
                            <AlertTriangle className="h-3 w-3 text-amber-600" />
                          )}
                          <button
                            onClick={() => downloadOnePdf(r.student_id, displayName)}
                            disabled={busy || !r.numero_candidat}
                            className="inline-flex items-center gap-1 text-xs bg-primary-600 text-white px-2.5 py-1 rounded hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            title={
                              r.numero_candidat
                                ? 'Télécharger le PDF du livret'
                                : 'N° candidat manquant'
                            }
                          >
                            {busyPdf ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Download className="h-3 w-3" />
                            )}
                            PDF
                          </button>
                          <button
                            onClick={() => downloadOneXlsx(r.student_id, displayName)}
                            disabled={busy || !r.numero_candidat}
                            className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 border border-gray-300 px-2.5 py-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                            title={
                              r.numero_candidat
                                ? 'Télécharger le livret Excel officiel'
                                : 'N° candidat manquant'
                            }
                          >
                            {busyXlsx ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <FileText className="h-3 w-3" />
                            )}
                            Excel
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLivretsBatch;
